import { NextPage } from 'next';
import type { AppProps } from 'next/app';
import Head from 'next/head';
import { ReactElement, ReactNode } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { preparePageLoadAnalytics } from '../lib/utils/analytics';
import AlertProvider from '../components/alert/alertProvider';
import { getCurrentEnv } from '../lib/utils/utils';
import '../register-web-components';
import '../styles/global.scss';
import '@digital-pulse/react/style.css';
import '@digital-pulse/design-tokens/blue.css';
//import '@pulse-community/typography-css/dist/main.css';
import { FeatureFlagWrapper } from '../components/featureFlagWrapper/featureFlagWrapper';
import Script from 'next/script';

global.XMLHttpRequest = require('xhr2');

type NextPageWithLayout = NextPage & {
  getLayout?: (pageProps: AppProps, page: ReactElement) => ReactNode;
};
type AppPropsWithLayout = AppProps & {
  Component: NextPageWithLayout;
};

const utagFolder = 'cvs';
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30 * 60 * 1000,
      refetchOnMount: false,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false
    }
  }
});
const currentEnv = getCurrentEnv();

function MyApp({ Component, pageProps }: AppPropsWithLayout | any) {
  if (typeof window !== 'undefined') {
    preparePageLoadAnalytics();
  }

  const env = currentEnv === '' || currentEnv === 'PROD' ? 'prod' : 'qa';

  return (
    <>
      <Head>
        <meta name="viewport" content="width=device-width, initial-scale=1"></meta>
        {typeof window !== 'undefined' && (
          <>
            <script
              type={'text/javascript'}
              src={`https://tags.tiqcdn.com/utag/cvs/${utagFolder}/${env}/utag.sync.js`}
            />
            <script
              id={'id-utag'}
              type={'text/javascript'}
              async={true}
              src={`https://tags.tiqcdn.com/utag/cvs/${utagFolder}/${env}/utag.js`}
            />
            <Script id={'id-adrum-config'}>
              (function()
              {
                (window['adrum-config-space'] =
                  env === 'prod' ? 'AD-AAB-ABG-SWZ' : 'AD-AAB-ACE-ZYJ')
              }
              )();
            </Script>
          </>
        )}
      </Head>
      <>
        <QueryClientProvider client={queryClient}>
          <ReactQueryDevtools initialIsOpen={false} />
          <AlertProvider>
            <Component {...pageProps} />
          </AlertProvider>
        </QueryClientProvider>
      </>
    </>
  );
}

export default FeatureFlagWrapper(MyApp);
